import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useState, useCallback } from 'react';
import { CampaignChannels, DirectMailSendInputState } from 'domains/campaigns/types';
import useDirectMails from 'hooks/queries/useDirectMails';
import { resetContent, setDirectMailContent } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import ContentSelector from 'domains/campaigns/components/CustomCampaign/components/ContentSelector';
import FileUploader from '../FileUploader';

enum ContentStepAction {
    SELECT = 'select',
    UPLOAD = 'upload',
}

export type Props = {
    activeSendIndex: number;
    contentPreviewId: number;
    currentSend: DirectMailSendInputState;
    dispatchCampaignAction: React.Dispatch<any>;
    setContentPreviewId: (id: number) => void;
    onContentUnavailableChange?: (isUnavailable: boolean, unavailableContentId?: number | null) => void;
};

const isUploadedPdf = (content: DirectMailSendInputState['content']) => {
    if (!content?.name) return false;
    return /\.pdf$/i.test(content.name);
};

const SelectOrUploadWrapper = ({
    activeSendIndex,
    currentSend,
    contentPreviewId,
    dispatchCampaignAction,
    setContentPreviewId,
    onContentUnavailableChange,
}: Props) => {
    const { name: fileName = '', id: contentId = null } = currentSend?.content || {};

    const [contentStepAction, setContentStepAction] = useState<ContentStepAction>(
        isUploadedPdf(currentSend?.content) ? ContentStepAction.UPLOAD : ContentStepAction.SELECT
    );

    const { data: directMails = [], isLoading: isLoadingDirectMails } = useDirectMails();
    const handleToggleChange = useCallback(
        (_e: React.MouseEvent<HTMLElement>, value: ContentStepAction | null) => {
            if (!value) return;
            setContentStepAction(value);
            dispatchCampaignAction(resetContent(activeSendIndex));
        },
        [dispatchCampaignAction, activeSendIndex]
    );

    const handleContentChange = useCallback(
        (contentId: number | null, contentName: string) => {
            dispatchCampaignAction(setDirectMailContent({ contentId, contentName }, activeSendIndex));

            if (contentPreviewId || !contentId) {
                setContentPreviewId(contentId);
            }
        },
        [dispatchCampaignAction, activeSendIndex, contentPreviewId, setContentPreviewId]
    );

    const handlePreviewChange = useCallback(() => {
        setContentPreviewId(contentPreviewId ? null : contentId);
    }, [contentPreviewId, contentId, setContentPreviewId]);

    return (
        <>
            <ToggleButtonGroup
                value={contentStepAction}
                exclusive
                onChange={handleToggleChange}
                aria-label="select or upload content"
            >
                <ToggleButton value={ContentStepAction.SELECT} aria-label="select or create content">
                    Select
                </ToggleButton>
                <ToggleButton value={ContentStepAction.UPLOAD} aria-label="upload from computer">
                    Upload
                </ToggleButton>
            </ToggleButtonGroup>

            {contentStepAction === ContentStepAction.UPLOAD ? (
                <FileUploader
                    activeSendIndex={activeSendIndex}
                    dispatchCampaignAction={dispatchCampaignAction}
                    fileName={fileName}
                />
            ) : (
                <ContentSelector
                    contentType={CampaignChannels.DIRECT_MAIL}
                    contentId={contentId}
                    contentList={directMails}
                    isLoading={isLoadingDirectMails}
                    isPreviewVisible={!!contentPreviewId}
                    onContentChange={handleContentChange}
                    onPreviewChange={handlePreviewChange}
                    onContentUnavailableChange={onContentUnavailableChange}
                />
            )}
        </>
    );
};

export default SelectOrUploadWrapper;
